import { nanoid } from 'nanoid';
import posthog, { type CaptureOptions, type PostHogConfig } from 'posthog-js';

import type { PostHogEventName } from '@/bootstrap/posthogEvents';
import type { StudentType } from '@/types/student';

import { isOptionalAnalyticsGranted } from '@/bootstrap/analyticsConsent';

type EventProperties = Record<string, unknown>;
type PrivacyMode = 'full' | 'strict';

const POSTHOG_API_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

const BASE_PROPERTIES = {
  funnel_name: 'course_to_timetable_2026_1',
  funnel_version: 1,
} as const;

const initialPrivacyMode: PrivacyMode = isOptionalAnalyticsGranted() ? 'full' : 'strict';
let personIdentifyEnabled = initialPrivacyMode === 'full';

const buildPrivacyConfig = (mode: PrivacyMode): Partial<PostHogConfig> => {
  if (mode === 'strict') {
    return {
      autocapture: {
        capture_copied_text: false,
      },
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
    };
  }

  return {
    autocapture: true,
    session_recording: {},
  };
};

const applyPrivacyMode = ({
  mode,
  resetWhenStrict,
}: {
  mode: PrivacyMode;
  resetWhenStrict: boolean;
}) => {
  personIdentifyEnabled = mode === 'full';
  posthog.set_config(buildPrivacyConfig(mode));

  if (mode === 'strict' && resetWhenStrict) {
    posthog.reset();
  }
};

const POSTHOG_OPTIONS: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24',
  capture_exceptions: true,
  debug: import.meta.env.MODE === 'development',
};

posthog.init(POSTHOG_API_KEY, POSTHOG_OPTIONS);

try {
  applyPrivacyMode({
    mode: initialPrivacyMode,
    resetWhenStrict: initialPrivacyMode === 'strict',
  });
} catch (error) {
  if (import.meta.env.DEV) {
    console.warn('[PostHog] initial privacy mode apply failed', {
      error,
      mode: initialPrivacyMode,
    });
  }
}

export const PostHogClient = posthog;

const sanitizeProperties = (properties: EventProperties = {}) => {
  return Object.fromEntries(
    Object.entries({
      ...BASE_PROPERTIES,
      ...properties,
    }).filter(([, value]) => value !== undefined),
  );
};

const capture = (
  event: PostHogEventName,
  properties: EventProperties = {},
  options?: CaptureOptions,
) => {
  try {
    posthog.capture(event, sanitizeProperties(properties), options);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] capture failed', { error, event });
    }
  }
};

const setOptionalAnalyticsConsent = (enabled: boolean) => {
  try {
    applyPrivacyMode({
      mode: enabled ? 'full' : 'strict',
      resetWhenStrict: !enabled,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] setOptionalAnalyticsConsent failed', { enabled, error });
    }
  }
};

export const PostHog = {
  capture,

  setOptionalAnalyticsConsent,

  setPersonIdentificationEnabled: (enabled: boolean) => {
    setOptionalAnalyticsConsent(enabled);
  },

  setUser: (student: StudentType) => {
    if (!personIdentifyEnabled) {
      return;
    }

    const profile = {
      department: student.department,
      double_major: student.doubleMajorDepartment ?? '',
      grade: student.grade,
      minor: student.minorDepartment ?? '',
      schoolId: student.schoolId,
      teaching: student.teachTrainingCourse,
    };

    posthog.identify(`${student.department}-${student.schoolId}-${student.grade}학년-${nanoid()}`);
    posthog.setPersonProperties(profile);
  },
};
